import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaCheck, FaFire, FaComment, 
  FaEye, FaHeart, FaCamera, FaVideo, FaStar,
  FaEnvelope, FaCalendar, FaGlobe, FaClock,
  FaChevronLeft, FaChevronRight, FaLock,
  FaShare, FaFlag, FaUserPlus, FaCheckCircle, FaPlay,
  FaCrown, FaSpinner
} from 'react-icons/fa';
import { Plyr } from 'plyr-react';
import 'plyr-react/plyr.css';
import { useAuth } from '../../context/AuthContext';
import profileService from '../../services/profile';
import messageService from '../../services/messages';
import favoriteService from '../../services/favorite';
import Loader from '../common/Loader';
import Boxview from '../common/Boxview';
import { useBoxview } from '../../hooks/useBoxview';
import toast from 'react-hot-toast';
import './profile.css';
import Modal from '../common/Modal';

const ProfileView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const boxview = useBoxview();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // États de chargement pour les actions
  const [liking, setLiking] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  const [followingAction, setFollowingAction] = useState(false);

  // Refs pour Plyr
  const plyrRefs = useRef({});

  const UPLOADS_URL = 'https://murima.julesferry-ga.com/uploads/';
  const isOwnProfile = currentUser?.id === parseInt(id);

  useEffect(() => {
    loadProfile();
  }, [id]);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Chargement du profil ID:', id);
      console.log('👤 Utilisateur connecté ID:', currentUser?.id);
      
      const result = await profileService.getProfile(id, currentUser?.id);
      
      console.log('📦 Données reçues:', result);
      
      if (result.success) {
        setProfile(result);
        setLiked(result.already_liked || false);
        setFavorite(result.is_favorite || false);
        setFollowing(result.is_following || false);
      } else {
        setError(result.error || 'Profil non trouvé');
        toast.error('Impossible de charger le profil');
      }
    } catch (error) {
      console.error('❌ Erreur chargement profil:', error);
      setError('Erreur de chargement');
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!currentUser) {
      toast.error('Connectez-vous pour liker');
      navigate('/login');
      return;
    }

    if (currentUser.id === parseInt(id)) {
      toast.error('Vous ne pouvez pas liker votre propre profil');
      return;
    }

    setLiking(true);
    try {
      const result = await profileService.likeUser(id);
      
      if (result.success) {
        setLiked(true);
        toast.success('Like envoyé ! ❤️');
        
        if (result.match) {
          showMatchNotification();
          toast.success('C\'est un match ! 🔥', { duration: 5000 });
        }
      } else {
        if (result.error === 'already_liked') {
          toast('Vous avez déjà liké ce profil', { icon: '💔' });
        } else {
          toast.error(result.error || 'Erreur lors du like');
        }
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setLiking(false);
    }
  };

  const handleFavorite = async () => {
    if (!currentUser) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      navigate('/login');
      return;
    }

    setFavoriting(true);
    try {
      if (favorite) {
        const result = await favoriteService.removeFavorite(currentUser.id, id);
        if (result.success) {
          setFavorite(false);
          toast.success('Retiré des favoris');
        } else {
          toast.error(result.error || 'Erreur lors du retrait');
        }
      } else {
        const result = await favoriteService.addFavorite(currentUser.id, id);
        if (result.success) {
          setFavorite(true);
          toast.success('Ajouté aux favoris ⭐');
        } else {
          toast.error(result.error || 'Erreur lors de l\'ajout');
        }
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setFavoriting(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error('Connectez-vous pour suivre');
      navigate('/login');
      return;
    }

    setFollowingAction(true);
    try {
      if (following) {
        const result = await profileService.unfollowUser(id);
        if (result.success) {
          setFollowing(false);
          toast.success('Vous ne suivez plus ce profil');
        } else {
          toast.error(result.error || 'Erreur lors du désabonnement');
        }
      } else {
        const result = await profileService.followUser(id);
        if (result.success) {
          setFollowing(true);
          toast.success('Vous suivez maintenant ce profil');
        } else {
          toast.error(result.error || 'Erreur lors de l\'abonnement');
        }
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setFollowingAction(false);
    }
  };

  const handleMessage = () => {
    if (!currentUser) {
      toast.error('Connectez-vous pour envoyer un message');
      navigate('/login');
      return;
    }
    navigate(`/messages?user=${id}`);
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `Profil de ${profile?.user?.pseudo}`,
        url: url
      }).catch(() => {
        navigator.clipboard.writeText(url);
        toast.success('Lien copié !');
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Lien copié dans le presse-papier !');
    }
  };

  const handleReport = () => {
    toast('Fonctionnalité de signalement bientôt disponible', {
      icon: '🚧'
    });
  };

  // Fonction pour ouvrir l'avatar dans Boxview
  const openAvatarBoxview = (e) => {
    e.stopPropagation();
    const avatarUrl = getImageUrl(profile?.user?.avatar);
    const imageList = [{
      url: avatarUrl,
      caption: `Avatar de ${profile?.user?.pseudo}`
    }];
    boxview.openBoxview(imageList, 0);
  };

  // Fonction pour ouvrir la cover dans Boxview
  const openCoverBoxview = (e) => {
    e.stopPropagation();
    const coverUrl = getImageUrl(profile?.user?.cover_photo || profile?.user?.cover);
    const imageList = [{
      url: coverUrl,
      caption: `Photo de couverture de ${profile?.user?.pseudo}`
    }];
    boxview.openBoxview(imageList, 0);
  };

  const openPhotoBoxview = (photos, index) => {
    const imageList = photos.map(photo => ({
      url: photo.url.startsWith('http') ? photo.url : `${UPLOADS_URL}${photo.url}`,
      caption: `Photo de ${profile?.user?.pseudo}`
    }));
    boxview.openBoxview(imageList, index);
  };

  const showMatchNotification = () => {
    const notification = document.createElement('div');
    notification.className = 'match-popup';
    notification.innerHTML = `
      <div class="match-content">
        <div class="match-hearts">❤️🔥❤️</div>
        <h2>C'est un MATCH !</h2>
        <p>Vous et ${profile?.user?.pseudo} vous êtes likés</p>
        <button onclick="window.location.href='/messages?user=${id}'">Envoyer un message</button>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 5000);
  };

  const getImageUrl = (path) => {
    if (!path) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return `${UPLOADS_URL}${path.substring(1)}`;
    return `${UPLOADS_URL}${path}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'Hors ligne';
    if (profile?.user?.en_ligne) return 'En ligne';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = Math.floor((now - date) / (1000 * 60));
      
      if (diff < 5) return 'En ligne à l\'instant';
      if (diff < 60) return `En ligne il y a ${diff} min`;
      if (diff < 120) return 'En ligne il y a 1h';
      if (diff < 1440) return `En ligne il y a ${Math.floor(diff / 60)}h`;
      return `En ligne le ${date.toLocaleDateString('fr-FR')}`;
    } catch (e) {
      return 'Hors ligne';
    }
  };

  const getVideoUrl = (video) => {
    if (!video?.url) return '';
    if (video.url.startsWith('http')) return video.url;
    return `${UPLOADS_URL}/${video.url}`;
  };

  const playVideo = (video) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  if (loading) return <Loader />;
  
  if (error) return (
    <div className="profile-error">
      <div className="error-icon">😕</div>
      <h2>Oups !</h2>
      <p>{error}</p>
      <button onClick={() => navigate(-1)}>Retour</button>
      <button onClick={loadProfile} style={{ marginLeft: '10px' }}>Réessayer</button>
    </div>
  );
  
  if (!profile || !profile.user) return (
    <div className="profile-error">
      <div className="error-icon">🔍</div>
      <h2>Profil introuvable</h2>
      <p>Ce profil n'existe pas ou a été supprimé</p>
      <button onClick={() => navigate('/dashboard')}>Accueil</button>
    </div>
  );

  const { user, interests, photos, videos } = profile;
  
  // Calculer l'âge si non fourni
  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return '?';
    try {
      const today = new Date();
      const birthDate = new Date(dateNaissance);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (e) {
      return '?';
    }
  };

  const age = user.age || calculateAge(user.date_naissance);

  return (
    <div className="profile-view">
      {/* Cover avec dégradé - RENDUE CLIQUABLE */}
      <div className="profile-cover" onClick={openCoverBoxview} style={{ cursor: 'pointer' }}>
        <img 
          src={getImageUrl(user.cover_photo || user.cover)} 
          alt="Cover" 
          className="cover-image"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1507525425510-56f2e7b4b9e3';
          }}
        />
        <div className="cover-gradient"></div>
        <div className="cover-actions" onClick={(e) => e.stopPropagation()}>
          <button className="cover-btn" onClick={() => navigate(-1)}>
            <FaChevronLeft />
          </button>
          <div className="cover-right">
            <button className="cover-btn" onClick={handleShare}>
              <FaShare />
            </button>
            {!isOwnProfile && (
              <button className="cover-btn" onClick={handleReport}>
                <FaFlag />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Header du profil */}
      <div className="profile-header">
        <div className="profile-avatar-wrapper">
          {/* Avatar RENDU CLIQUABLE */}
          <div className="profile-avatar" onClick={openAvatarBoxview} style={{ cursor: 'pointer' }}>
            <img 
              src={getImageUrl(user.avatar)} 
              alt={user.pseudo}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
              }}
            />
            {user.en_ligne && (
              <span className="online-badge" title="En ligne">
                <span className="pulse"></span>
              </span>
            )}
          </div>
          {!isOwnProfile && (
            <button 
              className={`follow-btn ${following ? 'following' : ''}`}
              onClick={handleFollow}
              disabled={followingAction}
            >
              {followingAction ? (
                <FaSpinner className="spinner" />
              ) : following ? (
                '✓ Suivi'
              ) : (
                'Suivre'
              )}
            </button>
          )}
        </div>

        <div className="profile-info">
          <div className="name-section">
            <h1>{user.pseudo || 'Utilisateur'}, <span className="age">{age}</span></h1>
            {user.is_verified && (
              <span className="verified-badge" title="Profil vérifié">
                <FaCheckCircle />
              </span>
            )}
            {user.is_premium && (
              <span className="premium-badge" title="Membre premium">
                <FaCrown />
              </span>
            )}
          </div>

          <p className="profile-location">
            <FaMapMarkerAlt /> {user.ville || 'Paris'}, {user.pays || 'France'}
          </p>

          <div className="profile-stats">
            <div className="stat">
              <span className="stat-value">{user.total_vues || 0}</span>
              <span className="stat-label">vues</span>
            </div>
            <div className="stat">
              <span className="stat-value">{user.total_likes || 0}</span>
              <span className="stat-label">likes</span>
            </div>
            <div className="stat">
              <span className="stat-value">{photos?.length || 0}</span>
              <span className="stat-label">photos</span>
            </div>
            <div className="stat">
              <span className="stat-value">{videos?.length || 0}</span>
              <span className="stat-label">vidéos</span>
            </div>
          </div>

          {!isOwnProfile && (
            <div className="profile-actions">
              <button 
                className={`action-btn like-btn ${liked ? 'liked' : ''}`}
                onClick={handleLike}
                disabled={liked || liking}
              >
                {liking ? (
                  <FaSpinner className="spinner" />
                ) : (
                  <FaHeart />
                )}
                <span>{liked ? 'Liké' : 'Liker'}</span>
              </button>
              <button 
                className="action-btn message-btn"
                onClick={handleMessage}
              >
                <FaComment /> Message
              </button>
              <button 
                className={`action-btn fav-btn ${favorite ? 'favorited' : ''}`}
                onClick={handleFavorite}
                disabled={favoriting}
              >
                {favoriting ? (
                  <FaSpinner className="spinner" />
                ) : (
                  <FaStar />
                )}
                <span>{favorite ? 'Favori' : 'Ajouter'}</span>
              </button>
            </div>
          )}

          {isOwnProfile && (
            <div className="profile-actions">
              <button 
                className="action-btn edit-btn"
                onClick={() => navigate('/my-profile')}
              >
                <FaUserPlus /> Modifier le profil
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          À propos
        </button>
        <button 
          className={`tab ${activeTab === 'photos' ? 'active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          Photos {photos?.length > 0 && `(${photos.length})`}
        </button>
        <button 
          className={`tab ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Vidéos {videos?.length > 0 && `(${videos.length})`}
        </button>
      </div>

      {/* Contenu des tabs */}
      <div className="tab-content">
        {/* Tab À propos */}
        {activeTab === 'about' && (
          <div className="about-tab">
            {/* Bio */}
            {user.bio && (
              <div className="info-card">
                <h3><FaHeart /> À propos de moi</h3>
                <p className="bio-text">{user.bio}</p>
              </div>
            )}

            {/* Informations détaillées */}
            <div className="info-card">
              <h3><FaCalendar /> Informations</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Âge</span>
                  <span className="info-value">{age} ans</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Genre</span>
                  <span className="info-value capitalize">{user.genre || 'Non spécifié'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Orientation</span>
                  <span className="info-value capitalize">{user.orientation || 'Non spécifiée'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Origine</span>
                  <span className="info-value">{user.origine || 'Non spécifiée'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Membre depuis</span>
                  <span className="info-value">{formatDate(user.date_inscription)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Dernière connexion</span>
                  <span className="info-value">{formatLastSeen(user.derniere_connexion)}</span>
                </div>
              </div>
            </div>

            {/* Centres d'intérêt */}
            {interests?.length > 0 && (
              <div className="info-card">
                <h3><FaStar /> Centres d'intérêt</h3>
                <div className="interests-cloud">
                  {interests.map(interest => (
                    <span key={interest.id} className="interest-tag">
                      {interest.nom}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Badges */}
            <div className="info-card">
              <h3><FaCheckCircle /> Badges</h3>
              <div className="badges-grid">
                {user.is_verified && (
                  <div className="badge-item">
                    <FaCheckCircle className="badge-icon verified" />
                    <span>Profil vérifié</span>
                  </div>
                )}
                {user.is_premium && (
                  <div className="badge-item">
                    <FaCrown className="badge-icon premium" />
                    <span>Membre premium</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Photos */}
        {activeTab === 'photos' && (
          <div className="photos-tab">
            {photos?.length > 0 ? (
              <div className="photos-grid">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id} 
                    className="photo-item"
                    onClick={() => openPhotoBoxview(photos, index)}
                  >
                    <img 
                      src={getImageUrl(photo.url)} 
                      alt={`Photo ${index + 1}`}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
                      }}
                    />
                    {photo.est_privee === 1 && (
                      <div className="private-overlay">
                        <FaLock />
                        <span>Privée</span>
                      </div>
                    )}
                    <div className="photo-hover">
                      <FaEye />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaCamera className="empty-icon" />
                <p>Aucune photo à afficher</p>
              </div>
            )}
          </div>
        )}

        {/* Tab Vidéos avec Plyr */}
        {activeTab === 'videos' && (
          <div className="videos-tab">
            {videos?.length > 0 ? (
              <div className="videos-grid">
                {videos.map(video => (
                  <div key={video.id} className="video-card">
                    <div className="video-thumbnail">
                      {/* Plyr dans la grille */}
                      <Plyr
                        ref={el => {
                          if (el) plyrRefs.current[video.id] = el;
                        }}
                        source={{
                          type: 'video',
                          sources: [
                            {
                              src: getVideoUrl(video),
                              type: 'video/mp4',
                            }
                          ]
                        }}
                        options={{
                          controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
                          autoplay: false,
                          muted: false,
                          volume: 1,
                          ratio: '16:9',
                          hideControls: false,
                          invertTime: false,
                          toggleInvert: false,
                          debug: false,
                          fullscreen: {
                            enabled: true,
                            fallback: true,
                            iosNative: true
                          },
                          loadSprite: true,
                          iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
                          blankVideo: 'https://cdn.plyr.io/static/blank.mp4'
                        }}
                      />
                 
                      
                      {video.est_privee && (
                        <div className="private-badge">
                          <FaLock /> Privé
                        </div>
                      )}
                      
                   
                    </div>

                    <div className="video-info">
                      <h4>{video.titre || 'Sans titre'}</h4>
                      <div className="video-meta">
                        <span><FaEye /> {video.vues || 0}</span>
                        <span><FaHeart /> {video.likes || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaVideo className="empty-icon" />
                <p>Aucune vidéo à afficher</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de lecture vidéo avec Plyr */}
      <Modal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setSelectedVideo(null);
        }}
        title={selectedVideo?.titre?.length > 32 
          ? `${selectedVideo?.titre.substring(0, 29)}...` 
          : selectedVideo?.titre}
        size="large"
      >
        {selectedVideo && showVideoModal && (
          <div className="video-player-container">
            <Plyr
              ref={el => {
                if (el) plyrRefs.current['modal'] = el;
              }}
              source={{
                type: 'video',
                sources: [
                  {
                    src: getVideoUrl(selectedVideo),
                    type: 'video/mp4',
                  }
                ]
              }}
              options={{
                controls: [
                  'play', 'progress', 'current-time', 'duration', 'mute', 'volume',
                  'captions', 'settings', 'pip', 'airplay', 'fullscreen'
                ],
                autoplay: true,
                muted: false,
                volume: 1,
                ratio: '16:9',
                hideControls: false,
                invertTime: false,
                toggleInvert: false,
                debug: false,
                fullscreen: {
                  enabled: true,
                  fallback: true,
                  iosNative: true
                },
                quality: {
                  default: 720,
                  options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240]
                },
                speed: {
                  selected: 1,
                  options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
                },
                loadSprite: true,
                iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
                blankVideo: 'https://cdn.plyr.io/static/blank.mp4'
              }}
            />
          </div>
        )}
      </Modal>

      {/* Boxview pour les photos */}
      <Boxview 
        isOpen={boxview.isOpen}
        onClose={boxview.closeBoxview}
        images={boxview.images}
        initialIndex={boxview.currentIndex}
      />
    </div>
  );
};

export default ProfileView;